import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { Customer } from 'src/app/common/Customer';
import { ApiServiceBase } from './ApiServiceBase';
import { FilePostDto } from '../common/FilePostDto';
import { CustomerContactUpdate } from '../common/CustomerContact';
import { PagedResult, PagingParams, pagingToHttpParams } from '../common/PagedResult';

@Injectable({
  providedIn: 'root',
})
export class CustomerService extends ApiServiceBase {
  protected readonly path = 'customer';

  // Dead endpoint: backend route POST /api/customer does not exist. Reevaluate later.
  create(customer: Customer): Observable<Customer> {
    return this.http
      .post<Customer>(this.getUrl(), customer);
  }

  getById(id: string): Observable<Customer> {
    return this.http
      .get<Customer>(this.getUrl() + '/' + id);
  }

  // Backend #670: a customer's admin maintains the contact block (e-mail, phone, fax,
  // website, description) here — the one customer write a non-Carrier can make. Returns
  // the fresh customer detail.
  updateContact(id: string, contact: CustomerContactUpdate): Observable<Customer> {
    return this.http
      .put<Customer>(this.getUrl() + '/' + id + '/contact', contact);
  }

  getSellerInfoByCustomerId(customerId: string): Observable<Customer> {
    return this.http
      .get<Customer>(this.getUrl() + '/sellerinfo/' + customerId);
  }

  getImageByProductId(id: string, largeImage: boolean): Observable<Blob> {
    return this.http
      .get(this.getUrl() + '/' + id + '/image', {
        responseType: 'blob',
        params: { largeImage: largeImage },
      });
  }

  getByfilter(name?: string, customerGroupId?: string, paging?: PagingParams): Observable<PagedResult<Customer>> {
    return this.http
      .get<PagedResult<Customer>>(this.getUrl(), {
        params: {
          name: name,
          customerGroupId: customerGroupId == null ? '' : customerGroupId,
          ...pagingToHttpParams(paging),
        },
      });
  }

  // #648: for lookup joins and pickers that need the whole customer set (the
  // browse grid pages; these don't). 500 is the backend's max page size and
  // comfortably exceeds the real customer count.
  getAllForLookup(): Observable<Customer[]> {
    return this.getByfilter('', null, { take: 500 }).pipe(map((r) => r.items));
  }

  updateMultipart(id: string, customer: Customer, fileTransactions: FilePostDto[]): Observable<Customer> {
    const formData: FormData = new FormData();

    // New files travel as multipart parts carrying the picked File's raw
    // binary (the backend binds them as IFormFile and stores the bytes as-is).
    fileTransactions.forEach((f) => {
      if (f.isCreated && f.filePayload?.rawFile) {
        formData.append('Documents', f.filePayload.rawFile, f.filePayload.originalFileName);
      }
    });

    const filesToDelete = fileTransactions
      .filter((f) => f.isDeleted && f.filePayload?.id)
      .map((f) => f.filePayload.id);

    const postPayload: any = {
      ...customer,
      files: undefined,
      filesToDelete,
    };

    formData.append('CustomerPostModel', JSON.stringify(postPayload));

    return this.http
      .put<Customer>(this.getUrl() + '/' + id, formData);
  }

  downloadFile(customerId: string, fileId: string): Observable<Blob> {
    return this.http
      .get(this.getUrl() + '/' + customerId + '/files/' + fileId, { responseType: 'blob' });
  }

  updateImage(id: string, largeImage: boolean, image: Blob): Observable<Customer> {
    const formData: FormData = new FormData();
    formData.append('Image', image, 'test');
    return this.http
      .put<Customer>(this.getUrl() + '/' + id + '/image', formData, {
        params: { largeImage: largeImage },
      });
  }
}
